/**
 * Constants ported verbatim from the original application (L84-99, L191-225, L395-420).
 */

export const TARGET_DEFAULT = 100000;
/** localStorage key used by the original single-file application. */
export const LEGACY_STORAGE_KEY = 'pirochi_pro_v2';

export type ProductKind = 'product' | 'tray';
export interface ProductSection {
  id: string;
  label: string;
  kind: ProductKind;
}

export const PRODUCT_SECTIONS: readonly ProductSection[] = [
  { id: 'chocolate', label: 'منتجات / شوكولاتة', kind: 'product' },
  { id: 'packages', label: 'الباقات', kind: 'product' },
  { id: 'savory', label: 'الموالح', kind: 'product' },
  { id: 'pastry', label: 'المعجنات', kind: 'product' },
  { id: 'zaffa', label: 'حلا الزفة', kind: 'product' },
  { id: 'drinks', label: 'المشروب', kind: 'product' },
  { id: 'other_services', label: 'خدمات أخرى', kind: 'product' },
  { id: 'tray', label: 'صواني المناسبات', kind: 'tray' },
  { id: 'tray_direct', label: 'صواني البيع المباشر', kind: 'tray' },
  { id: 'tray_app', label: 'صواني التطبيقات', kind: 'tray' },
];

export function sectionLabel(id: string | undefined | null): string {
  const s = PRODUCT_SECTIONS.find((x) => x.id === id);
  return s ? s.label : id || '—';
}
export function sectionKind(id: string | undefined | null): ProductKind {
  const s = PRODUCT_SECTIONS.find((x) => x.id === id);
  return s ? s.kind : 'product';
}

export type AccountType = 'asset' | 'liability' | 'equity' | 'income' | 'expense';
export interface AccountDef {
  code: string;
  name: string;
  type: AccountType;
}
/** Chart of accounts (Odoo-style) — L211-225. */
export const COA: readonly AccountDef[] = [
  { code: '1000', name: 'الصندوق / النقدية', type: 'asset' },
  { code: '1100', name: 'البنك', type: 'asset' },
  { code: '1200', name: 'الذمم المدينة (العملاء)', type: 'asset' },
  { code: '1300', name: 'المخزون', type: 'asset' },
  { code: '1400', name: 'ضريبة مدخلات (VAT قابلة للخصم)', type: 'asset' },
  { code: '2000', name: 'الذمم الدائنة (الموردون)', type: 'liability' },
  { code: '2100', name: 'ضريبة مخرجات (VAT مستحقة)', type: 'liability' },
  { code: '3000', name: 'رأس المال', type: 'equity' },
  { code: '4000', name: 'إيرادات المبيعات', type: 'income' },
  { code: '4100', name: 'إيرادات نقطة البيع', type: 'income' },
  { code: '5000', name: 'تكلفة البضاعة المباعة', type: 'expense' },
  { code: '5100', name: 'مشتريات', type: 'expense' },
  { code: '5200', name: 'مصروفات تشغيلية', type: 'expense' },
];

export const OPPORTUNITY_STAGES = ['lead', 'qualified', 'quotation', 'negotiation', 'won', 'lost'] as const;
export type OpportunityStage = (typeof OPPORTUNITY_STAGES)[number];
export const STAGE_L: Record<OpportunityStage, string> = {
  lead: 'فرصة جديدة',
  qualified: 'مؤهلة',
  quotation: 'عرض سعر',
  negotiation: 'تفاوض',
  won: 'مكسوبة',
  lost: 'مفقودة',
};
export const STAGE_C: Record<OpportunityStage, string> = {
  lead: 'bg-slate-100 text-slate-700',
  qualified: 'bg-sky-50 text-sky-700',
  quotation: 'bg-violet-50 text-violet-700',
  negotiation: 'bg-amber-50 text-amber-700',
  won: 'bg-emerald-50 text-emerald-700',
  lost: 'bg-rose-50 text-rose-700',
};

/** Main order workflow statuses (OS_L, L397). */
export const MAIN_ORDER_STATUSES = [
  'received',
  'confirmed',
  'in_preparation',
  'quality_checked',
  'ready',
  'delivered',
  'cancelled',
] as const;
/**
 * Additional statuses the original can write: logistics dropdown (L5951-5955) and
 * customer "reorder" (L1890). See SRC-04 / SRC-05.
 */
export const EXTRA_ORDER_STATUSES = ['new', 'preparing', 'out_for_delivery', 'completed'] as const;
export const ORDER_STATUSES = [...MAIN_ORDER_STATUSES, ...EXTRA_ORDER_STATUSES] as const;
export type OrderStatus = (typeof ORDER_STATUSES)[number];

export const OS_L: Record<string, string> = {
  received: 'تم الاستلام',
  confirmed: 'تم التأكيد',
  in_preparation: 'تحت التجهيز',
  quality_checked: 'مراجعة الجودة',
  ready: 'جاهز',
  delivered: 'تم التسليم',
  cancelled: 'ملغي',
};
export const OS_C: Record<string, string> = {
  received: 'bg-slate-100 text-slate-700',
  confirmed: 'bg-sky-50 text-sky-700',
  in_preparation: 'bg-amber-50 text-amber-700',
  quality_checked: 'bg-violet-50 text-violet-700',
  ready: 'bg-emerald-50 text-emerald-700',
  delivered: 'bg-green-100 text-green-800',
  cancelled: 'bg-rose-50 text-rose-700',
};
/** Labels of the logistics dropdown (L5951-5955) — used by the logistics page only. */
export const LOGISTICS_STATUS_OPTIONS: ReadonlyArray<[string, string]> = [
  ['received', 'مستلم'],
  ['preparing', 'تحضير'],
  ['out_for_delivery', 'خرج للتوصيل'],
  ['delivered', 'تم التسليم'],
  ['completed', 'مكتمل'],
];

export const CHECKLIST: readonly string[] = [
  'اسم العميل مطابق',
  'رقم الطلب صحيح',
  'المنتجات والكميات صحيحة',
  'الصينية / التنسيق مطابق',
  'التغليف والبطاقة جاهزة',
  'المناسبة والتاريخ صحيحان',
  'وقت التسليم مؤكد',
  'المتطلبات الخاصة منفذة',
  'مراجعة الجودة النهائية تمت',
];

export interface NavItem {
  id: string;
  label: string;
  g: string;
}
/** Sidebar navigation — order and grouping exactly as L400-420. */
export const NAV: readonly NavItem[] = [
  { id: 'dashboard', label: 'لوحة القيادة', g: 'رئيسي' },
  { id: 'ai', label: 'المساعد الذكي', g: 'رئيسي' },
  { id: 'dayBoard', label: 'يوم المناسبة', g: 'رئيسي' },
  { id: 'calendar', label: 'التقويم التشغيلي', g: 'رئيسي' },
  { id: 'pipeline', label: 'مسار المبيعات', g: 'المبيعات' },
  { id: 'customers', label: 'العملاء والأرشيف', g: 'المبيعات' },
  { id: 'orders', label: 'الطلبات', g: 'العمليات' },
  { id: 'logistics', label: 'التوصيل واللوجستيك', g: 'العمليات' },
  { id: 'products', label: 'المنتجات والصواني', g: 'العمليات' },
  { id: 'pricing', label: 'تسعير المنتجات', g: 'العمليات' },
  { id: 'pos', label: 'نقطة البيع POS', g: 'العمليات' },
  { id: 'commission', label: 'العمولة والإقفال', g: 'المالية' },
  { id: 'finance', label: 'المالية والتحصيل', g: 'المالية' },
  { id: 'accounting', label: 'المحاسبة والضريبة', g: 'المالية' },
  { id: 'suppliers', label: 'الموردين والمشتريات', g: 'المالية' },
  { id: 'inventory', label: 'إدارة المخزون الذكي', g: 'المالية' },
  { id: 'team', label: 'الفريق', g: 'رئيسي' },
  { id: 'attendance', label: 'دوام الموظفين', g: 'رئيسي' },
  { id: 'reports', label: 'التقارير', g: 'التقارير' },
];

export const PAY_METHODS = ['cash', 'card', 'transfer'] as const;
export type PayMethod = (typeof PAY_METHODS)[number];
export const PAY_METHOD_LABELS: Record<string, string> = { cash: 'كاش', card: 'شبكة', transfer: 'تحويل بنكي' };

export const CUSTOMER_TYPES = ['شركات', 'فنادق', 'مطاعم', 'مقاهي', 'جملة', 'فعاليات', 'تجزئة', 'أخرى'] as const;
export const CUSTOMER_SEGMENTS = ['قيمة عالية', 'منتظم', 'محتمل'] as const;

export const ATTENDANCE_TYPES: Record<string, string> = {
  work: 'حضور عمل',
  ot: 'إضافي فقط',
  leave: 'إجازة',
  absent: 'غياب',
  permission: 'استئذان',
  remote: 'عن بُعد',
};

export const APPOINTMENT_STATUS_L: Record<string, string> = {
  scheduled: 'مجدول',
  done: 'تم',
  cancelled: 'ملغي',
  no_show: 'لم يحضر',
};
export const APPOINTMENT_STATUS_C: Record<string, string> = {
  scheduled: 'bg-sky-50 text-sky-700',
  done: 'bg-emerald-50 text-emerald-700',
  cancelled: 'bg-rose-50 text-rose-700',
  no_show: 'bg-amber-50 text-amber-700',
};

export const COMPLAINT_STATUS_L: Record<string, string> = {
  open: 'مفتوحة',
  investigating: 'تحقيق',
  resolved: 'تم الحل',
  closed: 'مغلقة',
};
export const COMPLAINT_STATUS_C: Record<string, string> = {
  open: 'bg-rose-50 text-rose-700',
  investigating: 'bg-amber-50 text-amber-700',
  resolved: 'bg-emerald-50 text-emerald-700',
  closed: 'bg-slate-100 text-slate-600',
};

export const LOST_TRAY_REASONS = ['لم تُرجع من المناسبة', 'كسرت / تالفة', 'ضائعة عند العميل', 'خطأ تسليم', 'أخرى'] as const;
export const STOCK_ADJUST_REASONS = ['تعديل يدوي', 'جرد', 'إرجاع صواني', 'تالف / هدر', 'إنتاج جديد'] as const;

export interface AppSettingsShape {
  monthlyTarget: number;
  companyName: string;
  companyNameEn: string;
  crNumber: string;
  vatNumber: string;
  companyAddress: string;
  companyCity: string;
  companyPhone: string;
  companyEmail: string;
  vatRate: number | string;
  pricesIncludeVat: boolean;
  fiscalYearStart: string;
  invoicePrefix: string;
  posPrefix: string;
  posTerminal?: string;
  posCashier?: string;
  behaviorFlags?: Record<string, boolean>;
}

/** Default settings (EMPTY.settings, L191-206). */
export const DEFAULT_SETTINGS: AppSettingsShape = {
  monthlyTarget: TARGET_DEFAULT,
  companyName: 'Pirochi Luxury Chocolate',
  companyNameEn: 'Pirochi Luxury Chocolate',
  crNumber: '',
  vatNumber: '',
  companyAddress: 'المملكة العربية السعودية',
  companyCity: '',
  companyPhone: '',
  companyEmail: '',
  vatRate: 15,
  pricesIncludeVat: false,
  fiscalYearStart: '01-01',
  invoicePrefix: 'INV',
  posPrefix: 'POS',
};

/** Collections of the original localStorage document (EMPTY, L191). */
export const LEGACY_COLLECTIONS = [
  'customers',
  'opportunities',
  'orders',
  'products',
  'seasonal',
  'inventory',
  'complaints',
  'team',
  'payments',
  'appointments',
  'stocktakes',
  'stockMovements',
  'lostTrays',
  'suppliers',
  'quotes',
  'purchases',
  'supplierPayments',
  'journal',
  'manualJournals',
  'attendance',
] as const;
export type LegacyCollection = (typeof LEGACY_COLLECTIONS)[number];

export * from './behavior-flags';
